import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Edit2,
    Ban,
    Plus,
    Phone,
    Mail,
    MapPin,
    Calendar,
    Clock,
    FileText,
    AlertCircle,
    CheckCircle2,
    Download,
    Eye,
    MoreVertical,
    User,
    History,
    TrendingUp,
    CreditCard,
    MessageSquare,
    Loader2,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { formatPrice } from "../../utils/productUtils";
import AdminCustomerService from "../../services/admin/AdminCustomerService";
import { CustomerDetailSkeleton } from "../../components/admin/customers/CustomersSkeleton";
import { useNotification } from "../../context/NotificationContext";

export default function CustomerDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [customer, setCustomer] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [activeTab, setActiveTab] = useState("history"); // history, notes

    const fetchCustomer = async () => {
        setIsLoading(true);
        try {
            const result = await AdminCustomerService.getDetail(id);
            if (result.status === "success") {
                setCustomer(result.data);
            }
        } catch (error) {
            showNotification({
                type: "failed",
                title: "Error",
                message: "Failed to load customer details."
            });
            navigate("/admin/customers");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomer();
    }, [id]);

    const handleToggleBlock = async () => {
        setIsUpdatingStatus(true);
        try {
            const result = await AdminCustomerService.toggleBlock(id);
            if (result.status === "success") {
                showNotification({
                    type: "success",
                    title: "Status Updated",
                    message: result.message
                });
                setCustomer(result.data);
            }
        } catch (error) {
            showNotification({
                type: "failed",
                title: "Action Failed",
                message: error.response?.data?.message || "Failed to toggle block status."
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const statusMap = {
        active: { label: "Active", color: "text-green-600 bg-green-50 border-green-100", icon: CheckCircle2 },
        overdue: { label: "Overdue", color: "text-red-600 bg-red-50 border-red-100", icon: AlertCircle },
        inactive: { label: "Inactive", color: "text-gray-500 bg-gray-50 border-gray-200", icon: Clock },
        blocked: { label: "Blocked", color: "text-red-700 bg-red-100 border-red-200", icon: Ban },
    };

    const invoiceStatusMap = {
        generated: { label: "Generated", color: "text-gray-600 bg-gray-100 border-gray-200" },
        approved: { label: "Approved", color: "text-teal-600 bg-teal-50 border-teal-100" },
        in_use: { label: "In Use", color: "text-purple-600 bg-purple-50 border-purple-100" },
        completed: { label: "Completed", color: "text-green-600 bg-green-50 border-green-100" },
        cancelled: { label: "Cancelled", color: "text-red-600 bg-red-50 border-red-100" },
    };

    if (isLoading) return <AdminLayout><CustomerDetailSkeleton /></AdminLayout>;
    if (!customer) return null;

    const StatIcon = statusMap[customer.status].icon;

    return (
        <AdminLayout>
            <div className="p-4 lg:p-8 max-w-[1200px] mx-auto space-y-8 pb-20">
                {/* Header Navigation */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-[#262626] rounded-xl transition-colors"
                        >
                            <ArrowLeft size={20} className="text-gray-500" />
                        </button>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-xl font-bold text-[#111111] dark:text-white font-poppins">
                                    {customer.name}
                                </h1>
                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border flex items-center gap-1 ${statusMap[customer.status].color}`}>
                                    <StatIcon size={10} />
                                    {statusMap[customer.status].label}
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">Customer ID: {customer.id}</p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 group/actions">
                        <button
                            onClick={() => navigate(`/admin/customers/${customer.id}/edit`)}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-sm font-bold text-[#111111] dark:text-white hover:bg-gray-50 dark:hover:bg-[#262626] transition-all active:scale-95"
                        >
                            <Edit2 size={16} />
                            <span>Edit Profile</span>
                        </button>
                        <button
                            onClick={handleToggleBlock}
                            disabled={isUpdatingStatus}
                            className={`flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-50 disabled:scale-100 ${customer.status === 'blocked' ? 'text-green-500 hover:bg-green-50' : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10'}`}
                        >
                            {isUpdatingStatus ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Ban size={16} />
                            )}
                            <span>{customer.status === 'blocked' ? 'Unblock' : 'Block'}</span>
                        </button>
                        <button
                            onClick={() => navigate("/admin/invoices/create", { state: { prefilledCustomer: { id: customer.id, name: customer.name, phone: customer.phone, email: customer.email || "", address: customer.address || "" } } })}
                            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#62397f] hover:bg-[#7C3AED] text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-purple-200 dark:shadow-none active:scale-95 whitespace-nowrap"
                        >
                            <Plus size={18} />
                            <span>Generate Invoice</span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Info Card */}
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
                            <h3 className="text-sm font-bold text-[#111111] dark:text-white mb-6 flex items-center gap-2">
                                <User size={18} className="text-[#62397f]" />
                                Customer Information
                            </h3>
                            <div className="space-y-4">
                                <div className="flex gap-3">
                                    <div className="p-2 rounded-lg bg-gray-50 dark:bg-[#262626]">
                                        <Phone size={16} className="text-gray-400" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">Phone Number</p>
                                        <p className="text-sm font-medium dark:text-white">{customer.phone}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <div className="p-2 rounded-lg bg-gray-50 dark:bg-[#262626]">
                                        <Mail size={16} className="text-gray-400" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">Email Address</p>
                                        <p className="text-sm font-medium dark:text-white break-all">{customer.email || "No email provided"}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <div className="p-2 rounded-lg bg-gray-50 dark:bg-[#262626]">
                                        <MapPin size={16} className="text-gray-400" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">Delivery Address</p>
                                        <p className="text-sm font-medium dark:text-white">{customer.address}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <div className="p-2 rounded-lg bg-gray-50 dark:bg-[#262626]">
                                        <Calendar size={16} className="text-gray-400" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">Date Registered</p>
                                        <p className="text-sm font-medium dark:text-white">{customer.created_at}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Stats Mini Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-purple-50 dark:bg-purple-900/10 p-4 rounded-2xl border border-purple-100 dark:border-purple-800/30">
                                <TrendingUp size={20} className="text-[#62397f] mb-2" />
                                <p className="text-[10px] font-bold text-purple-400 uppercase">Total Revenue</p>
                                <p className="text-lg font-bold text-[#62397f]">₦{formatPrice(customer.stats.total_revenue)}</p>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/10 p-4 rounded-2xl border border-green-100 dark:border-green-800/30">
                                <History size={20} className="text-green-500 mb-2" />
                                <p className="text-[10px] font-bold text-green-400 uppercase">Total Rentals</p>
                                <p className="text-lg font-bold text-green-600">{customer.stats.total_rentals}</p>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Dynamic Sections */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Stats Summary Bar */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-white dark:bg-[#1A1A1A] p-5 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] text-center">
                                <p className="text-[10px] font-bold text-gray-400 uppercase">Active</p>
                                <p className="text-xl lg:text-sm font-bold dark:text-white">{customer.stats.active_rentals}</p>
                            </div>
                            <div className="bg-white dark:bg-[#1A1A1A] p-5 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] text-center">
                                <p className="text-[10px] font-bold text-gray-400 uppercase">Completed</p>
                                <p className="text-xl lg:text-sm font-bold dark:text-white">{customer.stats.completed_rentals}</p>
                            </div>
                            <div className="bg-white dark:bg-[#1A1A1A] p-5 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] text-center">
                                <p className="text-[10px] font-bold text-red-400 uppercase">Overdue</p>
                                <p className="text-xl lg:text-sm font-bold text-red-500">{customer.stats.overdue_rentals}</p>
                            </div>
                            <div className="bg-white dark:bg-[#1A1A1A] p-5 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] text-center flex items-center justify-center">
                                <div className="w-full">
                                    <p className="text-[10px] font-bold text-purple-400 uppercase">Loyalty</p>
                                    <div className="flex items-center justify-center gap-1">
                                        <CheckCircle2 size={14} className="text-[#62397f] shrink-0" />
                                        <p className="text-xl lg:text-sm font-bold text-[#62397f] leading-tight break-words">
                                            {customer.stats.total_revenue > 1000000 ? "Gold Tier" : "Standard Tier"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tabs Navigation */}
                        <div className="flex border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                            <button
                                onClick={() => setActiveTab("history")}
                                className={`px-6 py-3 text-sm font-bold transition-all relative ${activeTab === "history" ? "text-[#62397f]" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"}`}
                            >
                                <span className="flex items-center gap-2">
                                    <CreditCard size={18} />
                                    Rental History
                                </span>
                                {activeTab === "history" && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#62397f] rounded-full" />}
                            </button>
                            <button
                                onClick={() => setActiveTab("notes")}
                                className={`px-6 py-3 text-sm font-bold transition-all relative ${activeTab === "notes" ? "text-[#62397f]" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"}`}
                            >
                                <span className="flex items-center gap-2">
                                    <MessageSquare size={18} />
                                    Admin Notes
                                </span>
                                {activeTab === "notes" && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#62397f] rounded-full" />}
                            </button>
                        </div>

                        {/* Tabs Content */}
                        <div className="min-h-[400px]">
                            {activeTab === "history" ? (
                                <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="bg-gray-50/50 dark:bg-[#212121] border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                                                    <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase">Invoice</th>
                                                    <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase">Items</th>
                                                    <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase">Period</th>
                                                    <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase">Status</th>
                                                    <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#E5E7EB] dark:divide-[#2A2A2A]">
                                                {customer.rentals.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="5" className="px-6 py-20 text-center text-gray-400">
                                                            No rentals found for this customer.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    customer.rentals.map((rental) => (
                                                        <tr key={rental.id} className="hover:bg-gray-50/50 dark:hover:bg-[#262626] transition-colors">
                                                            <td className="px-6 py-4">
                                                                <span
                                                                    onClick={() => navigate(`/admin/invoices/${rental.id}`)}
                                                                    className="text-sm font-bold text-[#62397f] hover:underline cursor-pointer"
                                                                >
                                                                    {rental.invoice_number}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-xs dark:text-white max-w-[200px] truncate">
                                                                {rental.items_count} {rental.items_count === 1 ? "Item" : "Items"}
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex flex-col text-[11px] text-gray-400">
                                                                    <span>{rental.start}</span>
                                                                    <span>{rental.end}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${invoiceStatusMap[rental.status].color}`}>
                                                                    {invoiceStatusMap[rental.status].label}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-right">
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <button
                                                                        onClick={() => navigate(`/admin/invoices/${rental.id}`)}
                                                                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#333333] rounded-lg transition-colors text-gray-400"
                                                                    >
                                                                        <Eye size={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4 text-center py-20 bg-white dark:bg-[#1A1A1A] rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A]">
                                    <MessageSquare size={48} className="mx-auto text-gray-200 mb-4" />
                                    <p className="text-gray-400">Notes system coming soon</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
